import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Mail, BookOpen, Briefcase, PieChart, ChevronRight,
  ChevronDown, Shield, Rocket, DollarSign, ClipboardList,
  Settings, Search, MessageSquare
} from 'lucide-react';

const HelpCenter = () => {
  const navigate = useNavigate();
  const [expandedCategories, setExpandedCategories] = useState(['getting-started']);
  const [searchQuery, setSearchQuery] = useState('');

  const categories = [
    {
      id: 'getting-started',
      name: 'Getting Started',
      icon: Rocket,
      color: 'bg-green-100 text-green-600',
      articles: [
        {
          title: 'Getting Started with SuddyFlow',
          description: 'New to SuddyFlow? Start here, and choose Simple or Standard mode',
          path: '/help/getting-started',
          badge: 'Start Here',
          badgeColor: 'bg-green-100 text-green-700'
        }
      ]
    },
    {
      id: 'billing-payments',
      name: 'Billing & Payments',
      icon: DollarSign,
      color: 'bg-blue-100 text-blue-600',
      articles: [
        {
          title: 'Accept Credit Card Payments',
          description: 'Set up Stripe to accept online payments from customers',
          path: '/help/payments',
          badge: null
        },
        {
          title: 'Creating Invoices',
          description: 'Create, text, email, print, and track customer invoices',
          path: '/help/invoices',
          badge: 'Updated',
          badgeColor: 'bg-blue-100 text-blue-700'
        },
        {
          title: 'Payment Reconciliation',
          description: 'Track Venmo, Zelle, and cash payments from customers',
          path: '/help/payment-reconciliation',
          badge: 'New',
          badgeColor: 'bg-green-100 text-green-700'
        }
      ]
    },
    {
      id: 'customers',
      name: 'Customers',
      icon: Users,
      color: 'bg-purple-100 text-purple-600',
      articles: [
        {
          title: 'Customer Portal',
          description: 'Give customers secure access to view jobs and invoices',
          path: '/help/customer-portal',
          badge: 'Popular',
          badgeColor: 'bg-blue-100 text-blue-700'
        },
        {
          title: 'Service Agreements',
          description: 'Send legally binding digital agreements to customers (Standard mode)',
          path: '/help/service-agreements',
          badge: null
        }
      ]
    },
    {
      id: 'jobs-services',
      name: 'Jobs & Services',
      icon: ClipboardList,
      color: 'bg-orange-100 text-orange-600',
      articles: [
        {
          title: 'Online Quote Acceptance',
          description: 'Let customers accept quotes and schedule service online (Standard mode)',
          path: '/help/quote-acceptance',
          badge: 'New',
          badgeColor: 'bg-purple-100 text-purple-700'
        },
        {
          title: 'Service Types & Pricing',
          description: 'Set up your pricing menu and service offerings',
          path: '/help/service-types',
          badge: 'Essential',
          badgeColor: 'bg-green-100 text-green-700'
        },
        {
          title: 'Email Templates',
          description: 'Customize automated emails sent to customers (Standard mode)',
          path: '/help/email-templates',
          badge: null
        }
      ]
    },
    {
      id: 'notifications',
      name: 'Notifications',
      icon: MessageSquare,
      color: 'bg-green-100 text-green-600',
      articles: [
        {
          title: 'Text Messages (SMS)',
          description: 'Get text alerts, and text quotes and invoices to your customers',
          path: '/help/sms-notifications',
          badge: 'Updated',
          badgeColor: 'bg-blue-100 text-blue-700'
        },
        {
          title: 'Email Notifications',
          description: 'Configure automated emails for jobs, invoices, and more',
          path: '/help/email-notifications',
          badge: null
        }
      ]
    },
    {
      id: 'reports-taxes',
      name: 'Reports & Taxes',
      icon: PieChart,
      color: 'bg-emerald-100 text-emerald-600',
      articles: [
        {
          title: 'Reports & Analytics',
          description: 'Understand your business with P&L, revenue trends, and more',
          path: '/help/reports',
          badge: null
        },
        {
          title: 'Tax Settings & Preparation',
          description: 'Set up sales tax, quarterly estimates, and Schedule C',
          path: '/help/tax-settings',
          badge: null
        }
      ]
    },
    {
      id: 'account-settings',
      name: 'Account & Settings',
      icon: Settings,
      color: 'bg-gray-100 text-gray-600',
      articles: [
        {
          title: 'Account Security',
          description: 'Change your password and set up two-factor authentication',
          path: '/help/account-security',
          badge: null
        },
        {
          title: 'Business Branding',
          description: 'Upload your logo and customize your brand colors',
          path: '/help/branding',
          badge: null
        },
        {
          title: 'Best Practices',
          description: 'Tips and tricks to get the most out of SuddyFlow',
          path: '/help/best-practices',
          badge: null
        }
      ]
    }
  ];

  // What's New banner. Each item links to the article that explains it.
  const whatsNew = [
    {
      title: 'Simple Mode',
      description: 'Quote-free workflow: complete a job and the invoice is made for you',
      path: '/help/getting-started',
      hover: 'hover:border-green-300'
    },
    {
      title: 'Text Invoices & Quotes',
      description: 'Send an invoice or quote to a customer by text, with one tap',
      path: '/help/sms-notifications',
      hover: 'hover:border-green-300'
    },
    {
      title: 'Online Quote Acceptance',
      description: 'Customers can accept quotes & schedule online',
      path: '/help/quote-acceptance',
      hover: 'hover:border-purple-300'
    }
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  // Filter articles based on search
  const filteredCategories = searchQuery.trim()
    ? categories.map(category => ({
        ...category,
        articles: category.articles.filter(article =>
          article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
          article.description.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(category => category.articles.length > 0)
    : categories;

  // Expand all categories when searching
  const displayCategories = searchQuery.trim()
    ? filteredCategories.map(c => c.id)
    : expandedCategories;

  // Calculate total articles
  const totalArticles = categories.reduce((sum, c) => sum + c.articles.length, 0);

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-2">Help Center</h1>
          <p className="text-gray-600">
            Everything you need to run your business with SuddyFlow
          </p>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <input
              type="text"
              placeholder="Search help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10"
            />
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          </div>
        </div>

        {/* What's New Banner */}
        <div className="mb-6 bg-gradient-to-r from-purple-50 to-green-50 border border-purple-200 rounded-lg p-4">
          <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
            ✨ What's New
          </h3>
          <div className="grid sm:grid-cols-3 gap-3">
            {whatsNew.map((item) => (
              <button
                key={item.title}
                onClick={() => navigate(item.path)}
                className={`text-left p-3 bg-white rounded-lg border border-gray-200 transition-colors ${item.hover}`}
              >
                <p className="font-medium text-gray-900 text-sm">{item.title}</p>
                <p className="text-xs text-gray-500">{item.description}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Categories */}
        <div className="space-y-3">
          {filteredCategories.map((category) => {
            const Icon = category.icon;
            const isExpanded = displayCategories.includes(category.id);

            return (
              <div
                key={category.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${category.color}`}>
                      <Icon size={20} />
                    </div>
                    <div className="text-left">
                      <h2 className="font-semibold text-gray-900">{category.name}</h2>
                      <p className="text-xs text-gray-500">{category.articles.length} article{category.articles.length !== 1 ? 's' : ''}</p>
                    </div>
                  </div>
                  {isExpanded ? (
                    <ChevronDown size={20} className="text-gray-400" />
                  ) : (
                    <ChevronRight size={20} className="text-gray-400" />
                  )}
                </button>

                {/* Articles */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {category.articles.map((article, index) => (
                      <button
                        key={article.path}
                        onClick={() => navigate(article.path)}
                        className={`w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors text-left ${
                          index !== category.articles.length - 1 ? 'border-b border-gray-100' : ''
                        }`}
                      >
                        <div className="flex-1 min-w-0 pl-13 ml-10">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-medium text-gray-900 text-sm">{article.title}</h3>
                            {article.badge && (
                              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${article.badgeColor}`}>
                                {article.badge}
                              </span>
                            )}
                          </div>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">{article.description}</p>
                        </div>
                        <ChevronRight size={18} className="text-gray-300 flex-shrink-0 ml-2" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* No Results */}
        {searchQuery && filteredCategories.length === 0 && (
          <div className="text-center py-12">
            <BookOpen className="mx-auto text-gray-300 mb-4" size={48} />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No articles found</h3>
            <p className="text-gray-500">Try a different search term</p>
          </div>
        )}

        {/* Quick Stats */}
        <div className="mt-8 grid grid-cols-3 gap-4">
          <div className="bg-blue-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-blue-600">{totalArticles}</p>
            <p className="text-xs text-blue-700">Help Articles</p>
          </div>
          <div className="bg-green-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-green-600">{categories.length}</p>
            <p className="text-xs text-green-700">Categories</p>
          </div>
          <div className="bg-purple-50 rounded-lg p-4 text-center">
            <p className="text-2xl font-bold text-purple-600">24/7</p>
            <p className="text-xs text-purple-700">Available</p>
          </div>
        </div>

        {/* Need More Help */}
        <div className="mt-8 bg-gradient-to-r from-primary-50 to-primary-100 rounded-lg p-6 text-center">
          <h2 className="text-xl font-bold text-gray-900 mb-2">
            Still Need Help?
          </h2>
          <p className="text-gray-700 mb-4 text-sm">
            Can't find what you're looking for? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href="mailto:support@suddyflow.com"
              className="btn btn-primary text-sm"
            >
              Email Support
            </a>
            <button className="btn btn-secondary text-sm">
              Watch Tutorials
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HelpCenter;